import React, { useState, memo, useCallback, useMemo } from 'react';
import type { ChatMessagePayload } from '@/lib/chat-socket';

/**
 * Parses message text and renders @mentions as highlighted spans.
 * Matches @username patterns (alphanumeric + underscores, 3-20 chars).
 */
function renderMessageText(text: string): React.ReactNode {
  const mentionRegex = /@([a-zA-Z0-9_]{3,20})/g;
  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = mentionRegex.exec(text)) !== null) {
    // Text before the mention
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    // The mention itself
    parts.push(
      <span key={match.index} className="text-primary font-semibold">
        @{match[1]}
      </span>
    );
    lastIndex = match.index + match[0].length;
  }

  // Remaining text after last mention
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return parts.length > 0 ? parts : text;
}

// VIP tier badge component
const TierBadge: React.FC<{ tierName: string | null; tierLevel: number; tierColor: string | null }> = ({
  tierName,
  tierLevel,
  tierColor,
}) => {
  if (!tierName || tierLevel <= 0) return null;

  return (
    <span
      className="inline-flex items-center gap-0.5 text-[10px] font-bold px-1 py-0.5 rounded leading-none flex-shrink-0"
      style={{
        color: tierColor || '#9CA3AF',
        backgroundColor: `${tierColor || '#9CA3AF'}15`,
      }}
    >
      {tierLevel >= 5 && <StarIcon color={tierColor} />}
      {tierLevel >= 3 && tierLevel < 5 && <ShieldIcon color={tierColor} />}
      {tierName}
    </span>
  );
};

const StarIcon: React.FC<{ color: string | null }> = ({ color }) => (
  <svg viewBox="0 0 12 12" className="w-2.5 h-2.5 flex-shrink-0" fill={color || '#9CA3AF'}>
    <path d="M6 1l1.5 3.1L11 4.5 8.5 7l.6 3.5L6 8.8 2.9 10.5l.6-3.5L1 4.5l3.5-.4z" />
  </svg>
);

const ShieldIcon: React.FC<{ color: string | null }> = ({ color }) => (
  <svg viewBox="0 0 12 12" className="w-2.5 h-2.5 flex-shrink-0" fill={color || '#9CA3AF'}>
    <path d="M6 1L2 3v3c0 2.5 1.7 4.7 4 5.5 2.3-.8 4-3 4-5.5V3L6 1z" />
  </svg>
);

const ModBadge: React.FC = () => (
  <span className="inline-flex items-center gap-0.5 text-[10px] font-bold px-1 py-0.5 rounded leading-none flex-shrink-0 bg-green-500/15 text-green-400">
    <svg viewBox="0 0 12 12" className="w-2.5 h-2.5 flex-shrink-0" fill="currentColor">
      <path d="M6 1L2 3v3c0 2.5 1.7 4.7 4 5.5 2.3-.8 4-3 4-5.5V3L6 1z" />
    </svg>
    MOD
  </span>
);

const FlagIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg viewBox="0 0 16 16" className={className} fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M2 2v12M2 2h8l-1.5 3L10 8H2" />
  </svg>
);

interface ChatMessageItemProps {
  message: ChatMessagePayload;
  onFlag?: (messageId: number) => void;
  onUnflag?: (messageId: number) => void;
}

export const ChatMessageItem: React.FC<ChatMessageItemProps> = memo(({ message, onFlag, onUnflag }) => {
  const [flagged, setFlagged] = useState(false);

  // Format time as HH:MM
  const time = new Date(message.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

  const handleToggleFlag = useCallback(() => {
    if (flagged) {
      if (!onUnflag) return;
      setFlagged(false);
      onUnflag(message.id);
    } else {
      if (!onFlag) return;
      setFlagged(true);
      onFlag(message.id);
    }
  }, [flagged, onFlag, onUnflag, message.id]);

  return (
    <div className="group flex gap-1.5 px-3 py-1 hover:bg-muted/30 transition-colors">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1 flex-wrap">
          {message.isModerator ? (
            <ModBadge />
          ) : (
            <TierBadge
              tierName={message.tierName}
              tierLevel={message.tierLevel}
              tierColor={message.tierColor}
            />
          )}
          <span
            className="text-xs font-semibold truncate"
            style={{ color: message.isModerator ? '#4ade80' : (message.tierColor || 'hsl(var(--primary))') }}
          >
            {message.username}:
          </span>
          <span className="text-xs text-foreground/80 break-words leading-relaxed">
            {renderMessageText(message.message)}
          </span>
        </div>
      </div>
      <div className="flex items-center gap-1 flex-shrink-0 pt-0.5">
        {/* Report flag button — hidden if admin already reviewed and kept */}
        {(onFlag || onUnflag) && !message.isReviewed && (
          flagged ? (
            <button
              onClick={handleToggleFlag}
              className="inline-flex items-center gap-0.5 text-amber-500 text-[10px] font-medium px-1 rounded hover:bg-amber-500/10 transition-colors"
              title="Undo report"
            >
              <FlagIcon className="w-3 h-3" />
              Reported
            </button>
          ) : (
            <button
              onClick={handleToggleFlag}
              className="opacity-0 group-hover:opacity-100 transition-opacity p-0.5 rounded text-muted-foreground hover:text-amber-500 hover:bg-amber-500/10"
              title="Report message"
            >
              <FlagIcon className="w-3 h-3" />
            </button>
          )
        )}
        <span className="text-[10px] text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity">
          {time}
        </span>
      </div>
    </div>
  );
});
